import * as React from "react";
import { UserIcon } from "lucide-react";
import { getInitialsForName } from "../../../lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import { useOrgUserOptional } from "../../../auth/org-provider";
import { useUserProfilePictureQuery } from "../../../data/queries/user-profile-picture";

function Fallback(props: { name: string; variant: "icon" | "initials" }) {
    if (props.variant === "icon") {
        return (
            <AvatarFallback>
                <UserIcon className={"aspect-square w-[70%] opacity-45"} />
            </AvatarFallback>
        );
    }
    return <AvatarFallback>{getInitialsForName(props.name)}</AvatarFallback>;
}

function LegacyAvatar(props: { name: string; userId: string; className?: string; fallback: "icon" | "initials" }) {
    const { data } = useUserProfilePictureQuery(props.userId);
    return (
        <Avatar className={props.className}>
            <AvatarImage src={data ? `data:image/png;base64,${data}` : undefined} />
            <Fallback name={props.name} variant={props.fallback} />
        </Avatar>
    );
}

function NewAvatar(props: { name: string; imageUrl: string; className?: string; fallback: "icon" | "initials" }) {
    return (
        <Avatar className={props.className}>
            <AvatarImage src={props.imageUrl} />
            <Fallback name={props.name} variant={props.fallback} />
        </Avatar>
    );
}

type AvatarFallback = "icon" | "initials";

export function AvatarForUser(props: {
    user: {
        id: string;
        name?: string;
        displayName?: string;
        imageUrl?: string;
    };
    fallback?: AvatarFallback;
    className?: string;
}) {
    const name = props.user.displayName ?? props.user.name ?? "";

    if (props.user.imageUrl) {
        return (
            <NewAvatar
                name={name}
                imageUrl={props.user.imageUrl}
                className={props.className}
                fallback={props.fallback ?? "initials"}
            />
        );
    } else {
        return (
            <LegacyAvatar
                name={name}
                userId={props.user.id}
                className={props.className}
                fallback={props.fallback ?? "initials"}
            />
        );
    }
}

export function UserAvatarAndName(props: {
    userId: string;
    avatarClassName?: string;
    nameClassName?: string;
    avatarFallback?: AvatarFallback;
}) {
    const orgUser = useOrgUserOptional(props.userId);
    if (!orgUser) {
        return <i className={props.nameClassName}>deleted user</i>;
    }
    return (
        <>
            <AvatarForUser fallback={props.avatarFallback} user={orgUser} className={props.avatarClassName} />
            <div className={props.nameClassName}>{orgUser.name}</div>
        </>
    );
}
